import { useState, useEffect } from 'react';
import './styles.scss';
import Button from '@/components/common/Button';
import { X, Shield, Users } from 'lucide-react';

import { REQUIRED_ROLES, ASSIGNABLE_ROLES } from '@/utils/roleUtils';

const EditUserModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  user,
  teams = []
}) => {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user && isOpen) {
      // Ensure required roles are ALWAYS included, plus whatever roles the user has
      const currentRoles = user.roles || [];
      const mergedRoles = Array.from(new Set([...REQUIRED_ROLES, ...currentRoles]));
      setSelectedRoles(mergedRoles);
      setSelectedTeam(user.teamId || '');
    }
  }, [user, isOpen]);

  if (!isOpen) return null;

  const handleToggleRole = (role) => {
    if (REQUIRED_ROLES.includes(role)) return; // Prevent toggling required roles

    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(user.id, selectedRoles, selectedTeam);
      onClose();
    } catch (err) {
      console.error('Failed to save user changes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const renderRoleCheckbox = (role, isRequired = false) => (
    <div 
      key={role} 
      className={`role-checkbox ${selectedRoles.includes(role) ? 'is-checked' : ''} ${isRequired ? 'is-required' : ''}`}
      onClick={() => handleToggleRole(role)}
    >
      <input 
        type="checkbox" 
        checked={selectedRoles.includes(role)}
        readOnly 
        disabled={isRequired}
      />
      <span>{role}</span>
    </div>
  );

  return (
    <div className="edit-user-modal-overlay" onClick={onClose}>
      <div className="edit-user-modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-info">
            <h3>Edit Institutional Registry</h3>
            <p>Modify roles and organization mapping for <strong>{user?.username}</strong></p>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label><Shield size={14} className="mr-s" /> Access Privileges</label>
            <div className="roles-grid">
              {/* Render Required Roles First */}
              {REQUIRED_ROLES.map(role => renderRoleCheckbox(role, true))}
              
              {/* Render Assignable Roles */}
              {ASSIGNABLE_ROLES.map(role => renderRoleCheckbox(role))}
            </div>
          </div>

          <div className="form-group">
            <label><Users size={14} className="mr-s" /> Institutional Team Assignment</label>
            <select 
              className="team-select"
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
            >
              <option value="">No Assignment (Orphaned)</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name} {team.organization ? `(${team.organization.name})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="modal-footer">
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} loading={isSaving}>
            Save Registry Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
